import type { InlineComment } from '@service/InlineCommentService';
import { subscribeInlineCommentEvents } from '@yjs/yjs-connector.js';

/** Must match server/yjs/inline-comment-broadcast.js */
export const MESSAGE_INLINE_COMMENT = 101;

export type InlineCommentWsEvent =
    | { action: 'created' | 'updated'; comment: InlineComment }
    | { action: 'deleted'; commentId: string };

type WsProvider = {
    ws: WebSocket | null;
    on: (event: string, handler: (arg: { status: string }) => void) => void;
    off?: (event: string, handler: (arg: { status: string }) => void) => void;
};

/**
 * Subscribe to inline-comment WS broadcasts via y-websocket messageHandlers.
 */
export function attachInlineCommentSync(
    noteId: string,
    _provider: WsProvider | null | undefined,
    onEvent: (event: InlineCommentWsEvent) => void,
): () => void {
    if (!noteId) return () => {};
    return subscribeInlineCommentEvents(noteId, onEvent as (event: object) => void);
}

export function normalizeCommentId(id: unknown): string {
    if (id == null) return '';
    if (typeof id === 'string') return id;
    if (typeof id === 'object' && id !== null && '_id' in id) {
        return String((id as { _id: unknown })._id);
    }
    return String(id);
}

export function applyLocalInlineCommentPermissions(
    comment: InlineComment,
    currentUserId?: string | null,
    noteOwnerId?: string | null,
    preserveMyReaction?: string | null,
): InlineComment {
    const authorId =
        typeof comment.author === 'object' && comment.author?.id
            ? String(comment.author.id)
            : String(comment.author ?? '');
    const uid = currentUserId ? String(currentUserId) : '';
    const ownerId = noteOwnerId ? String(noteOwnerId) : '';

    return {
        ...comment,
        id: normalizeCommentId(comment.id),
        myReaction: preserveMyReaction ?? comment.myReaction ?? null,
        isOwner: authorId === uid,
        canDelete: authorId === uid,
        canResolve: ownerId === uid,
    };
}
